import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Circle, ArrowRight } from 'lucide-react';

const checklistSections = [
  {
    title: "Individual Filing Essentials",
    items: [
      { id: 'filing-status', label: "Confirm your optimal filing status (single, married joint, married separate, head of household)" },
      { id: 'w2', label: "Gather all W-2s, 1099s, and income documents" },
      { id: 'life-events', label: "Document life changes that affect your return (marriage, divorce, new child, home purchase)" },
      { id: 'deductions', label: "Review potential deductions (mortgage interest, state taxes, medical expenses)" },
      { id: 'credits', label: "Check eligibility for credits (Child Tax Credit, Earned Income Credit, education credits)" }
    ]
  },
  {
    title: "Income & Investments",
    items: [
      { id: 'side-income', label: "Report all side income, freelance work, or gig economy earnings" },
      { id: 'investments', label: "Review investment gains/losses and consider tax-loss harvesting" },
      { id: 'rental', label: "Gather rental property income and expense records (if applicable)" },
      { id: 'hsa', label: "Maximize HSA, FSA, and retirement account contributions" },
      { id: 'roth', label: "Evaluate Roth conversion opportunities" }
    ]
  },
  {
    title: "Business Owner Essentials",
    items: [
      { id: 'entity', label: "Review business entity structure (LLC vs S-Corp vs C-Corp)" },
      { id: 'quarterly', label: "Verify quarterly estimated tax payments are current" },
      { id: 'retirement-biz', label: "Evaluate business retirement plan options (SEP-IRA, Solo 401k)" },
      { id: 'expenses', label: "Categorize and document all business expenses" }
    ]
  },
  {
    title: "Year-End Planning & Compliance",
    items: [
      { id: 'withholding', label: "Review W-4 withholding to avoid underpayment penalties" },
      { id: 'gifting', label: "Plan gifting strategy to minimize estate taxes" },
      { id: 'state', label: "Check state filing requirements (especially if multi-state income)" },
      { id: 'notices', label: "Address any outstanding IRS or state notices" },
      { id: 'beneficiaries', label: "Update beneficiaries on retirement and insurance accounts" }
    ]
  }
];

export const TaxChecklist = ({ isOpen, onClose }) => {
  const [checked, setChecked] = useState({});
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState(false);

  const toggle = (id) => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const totalItems = checklistSections.reduce((acc, s) => acc + s.items.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progress = Math.round((checkedCount / totalItems) * 100);

  const handleSave = (e) => {
    e.preventDefault();
    setSaved(true);
  };

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="tax-checklist-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="tax-checklist-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#0B2240] p-6 flex items-start justify-between">
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white">
                Tax Planning Checklist
              </h3>
              <p className="text-white/60 text-sm mt-1">
                Track your progress — {checkedCount} of {totalItems} complete
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-white/60 hover:text-white transition-colors"
              data-testid="tax-checklist-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-[#D4AF37] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-6 space-y-8">
            {checklistSections.map((section) => (
              <div key={section.title}>
                <h4 className="font-semibold text-[#0B2240] text-sm uppercase tracking-wide mb-3">
                  {section.title}
                </h4>
                <div className="space-y-2">
                  {(section.items || []).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => toggle(item.id)}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-200 ${
                        checked[item.id]
                          ? 'bg-[#1FA3A3]/8 border border-[#1FA3A3]/20'
                          : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                      }`}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      {checked[item.id] ? (
                        <CheckCircle2 className="h-5 w-5 text-[#1FA3A3] flex-shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${
                        checked[item.id] ? 'text-[#0B2240] line-through opacity-70' : 'text-[#0B2240]'
                      }`}>
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Save progress / CTA */}
            <div className="border-t border-gray-100 pt-6">
              {!saved ? (
                <div>
                  <p className="text-sm text-[#4C5A70] mb-3">
                    Want a personalized review of your tax situation? Our advisors can help you prioritize what matters most.
                  </p>
                  <button
                    onClick={scrollToContact}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[#0B2240] font-semibold text-sm bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] hover:brightness-110 transition-all"
                    data-testid="checklist-schedule-cta"
                  >
                    Schedule a Free Strategy Session
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[#1FA3A3]">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-medium">Your progress has been noted. Keep going!</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
